import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { myFirebaseAuth } from '../../firebase/config';
import { logoutUser } from './authService';
import {
  CollectionSequence,
  CollectionSequenceBuyerProgress,
  CollectionSequenceCreate,
  CollectionSequenceUpdate,
} from '@dill/dill-shared';

export interface CollectionSequenceEnrollment {
  sequence: CollectionSequence;
  progress: CollectionSequenceBuyerProgress;
}

export const getSupplierCollectionSequences = createAsyncThunk(
  'collectionSequences/getSupplierCollectionSequences',
  async ({ supplierId }: { supplierId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionSequences/getSupplierCollectionSequences`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createCollectionSequence = createAsyncThunk(
  'collectionSequences/createCollectionSequence',
  async (
    { supplierId, data }: { supplierId: string; data: CollectionSequenceCreate },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionSequences/createCollectionSequence`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const updateCollectionSequence = createAsyncThunk(
  'collectionSequences/updateCollectionSequence',
  async (
    {
      supplierId,
      sequenceId,
      data,
    }: { supplierId: string; sequenceId: string; data: CollectionSequenceUpdate },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionSequences/${sequenceId}/updateCollectionSequence`,
        data,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const deleteCollectionSequence = createAsyncThunk(
  'collectionSequences/deleteCollectionSequence',
  async ({ supplierId, sequenceId }: { supplierId: string; sequenceId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionSequences/${sequenceId}/deleteCollectionSequence`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

// Per-buyer enrollment: a buyer has at most one active/paused sequence enrollment at a time
// (global mutual-exclusion rule), but the endpoint stays array-shaped (0 or 1 entries) so the
// customer-page component can render a list either way.

export const enrollCollectionSequenceEnrollment = createAsyncThunk(
  'collectionSequences/enrollCollectionSequenceEnrollment',
  async (
    {
      supplierId,
      sequenceId,
      buyerId,
    }: { supplierId: string; sequenceId: string; buyerId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionSequences/${sequenceId}/buyer/${buyerId}/enroll`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

export const getBuyerCollectionSequenceEnrollments = createAsyncThunk(
  'collectionSequences/getBuyerCollectionSequenceEnrollments',
  async ({ supplierId, buyerId }: { supplierId: string; buyerId: string }, thunkApi) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionSequences/buyer/${buyerId}/getBuyerCollectionSequenceEnrollments`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const pauseCollectionSequenceEnrollment = createAsyncThunk(
  'collectionSequences/pauseCollectionSequenceEnrollment',
  async (
    {
      supplierId,
      sequenceId,
      buyerId,
    }: { supplierId: string; sequenceId: string; buyerId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionSequences/${sequenceId}/buyer/${buyerId}/pause`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const resumeCollectionSequenceEnrollment = createAsyncThunk(
  'collectionSequences/resumeCollectionSequenceEnrollment',
  async (
    {
      supplierId,
      sequenceId,
      buyerId,
    }: { supplierId: string; sequenceId: string; buyerId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionSequences/${sequenceId}/buyer/${buyerId}/resume`,
        {},
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const unenrollCollectionSequenceEnrollment = createAsyncThunk(
  'collectionSequences/unenrollCollectionSequenceEnrollment',
  async (
    {
      supplierId,
      sequenceId,
      buyerId,
    }: { supplierId: string; sequenceId: string; buyerId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.delete<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/collectionSequences/${sequenceId}/buyer/${buyerId}/unenroll`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.log(error?.message);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
